/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

import React from "react"
import { I18nextProvider } from "react-i18next"
import { LanguageProvider } from "./src/contexts/LanguageContext"
import "./src/styles/globals.css"
import "./src/styles/pretendard.css"
import i18n from "./src/i18n"
import { initLinkInterceptor } from "./src/utils/linkInterceptor"
import {
  initGaSessionHandoff,
  initGoogleAdsTracking,
  refreshGaSessionCookie,
} from "./src/utils/tracking"
import { initPosthog } from "./src/utils/posthog"

const clearLegacyBrowserCaches = () => {
  if (typeof window === "undefined") return

  window.__KLANG_CLIENT_RUNTIME_VERSION__ = "2026-05-27-cache-cleanup-v1"

  const cleanupKey = "klang:legacy-browser-cache-cleaned:v1"
  try {
    if (window.localStorage.getItem(cleanupKey) === "done") return
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. The
    // cleanup is optional, so never let it block the application runtime.
    return
  }

  const hadServiceWorkerController = Boolean(
    navigator.serviceWorker?.controller
  )

  const unregisterServiceWorkers =
    "serviceWorker" in navigator
      ? navigator.serviceWorker
          .getRegistrations()
          .then(registrations =>
            Promise.all(
              registrations.map(registration => registration.unregister())
            )
          )
      : Promise.resolve()

  const clearCaches =
    "caches" in window
      ? window.caches
          .keys()
          .then(keys => Promise.all(keys.map(key => window.caches.delete(key))))
      : Promise.resolve()

  Promise.all([unregisterServiceWorkers, clearCaches])
    .then(() => {
      window.localStorage.setItem(cleanupKey, "done")
      if (hadServiceWorkerController) {
        window.location.reload()
      }
    })
    .catch(error => {
      console.warn("Failed to clear legacy browser caches", error)
    })
}

// Set the language synchronously from the page's locale BEFORE React hydrates,
// mirroring gatsby-ssr.js's wrapPageElement. Without this, the first client
// render uses the fallback language (en) while the server produced sv/no markup,
// causing React hydration mismatches (#418/#423/#425) and a full re-render flash.
// LanguageProvider is mounted here (inside the Router) rather than at the root so
// its useLocation resolves the page path identically on server and client.
export const wrapPageElement = ({ element, props }) => {
  const locale = props?.pageContext?.locale
  if (locale && i18n.language !== locale) {
    i18n.changeLanguage(locale)
  }
  return (
    <I18nextProvider i18n={i18n}>
      <LanguageProvider>{element}</LanguageProvider>
    </I18nextProvider>
  )
}

export const onClientEntry = () => {
  clearLegacyBrowserCaches()

  // Disable browser's native scroll restoration
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual"
  }

  // Initialize Google Ads tracking on page load
  initGoogleAdsTracking()
  initGaSessionHandoff()

  // Initialize PostHog analytics
  initPosthog()

  // Initialize the global link interceptor
  initLinkInterceptor()
}

export const onRouteUpdate = () => {
  refreshGaSessionCookie()
}

// Handle scroll position on route changes:
// - Back/forward navigation: restore the saved scroll position
// - New navigation: scroll to top
// Use 'instant' to override CSS scroll-behavior: smooth.
export const shouldUpdateScroll = ({
  routerProps: { location },
  getSavedScrollPosition,
}) => {
  const savedPosition = getSavedScrollPosition(location)
  const target = savedPosition || [0, 0]
  window.requestAnimationFrame(() => {
    window.scrollTo({ left: target[0], top: target[1], behavior: "instant" })
  })
  return false
}
